/**
 * Heuristic source classification from UTM params, click IDs, and referrer.
 */

const PAID_MEDIUMS = new Set(['cpc', 'ppc', 'paid', 'paidsearch', 'display', 'retargeting']);

function normalizeHost(referrer) {
  if (!referrer || typeof referrer !== 'string') return '';
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    return host.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function classifyFromReferrer(host) {
  if (!host) return null;
  if (host.includes('google.')) return 'google_organic';
  if (host.includes('youtube.com') || host.includes('youtu.be')) return 'youtube';
  if (host.includes('reddit.com')) return 'reddit';
  if (host.includes('bing.com')) return 'bing_organic';
  if (host.includes('facebook.com') || host.includes('fb.com') || host.includes('instagram.com')) {
    return 'facebook';
  }
  if (host.includes('linkedin.com')) return 'linkedin';
  if (host.includes('twitter.com') || host.includes('t.co') || host.includes('x.com')) return 'twitter';
  if (
    host.includes('chatgpt.com') ||
    host.includes('openai.com') ||
    host.includes('perplexity.ai') ||
    host.includes('claude.ai') ||
    host.includes('gemini.google.com')
  ) {
    return 'ai_referral';
  }
  return `referral_${host.split('.')[0]}`;
}

/**
 * @param {object} touch - attribution touch with utm_*, gclid, referrer, etc.
 * @returns {string}
 */
export function classifySource(touch) {
  if (!touch || typeof touch !== 'object') return 'direct';

  const gclid = touch.gclid || touch.gbraid || touch.wbraid;
  if (gclid) return 'google_cpc';

  const medium = String(touch.utm_medium || '').toLowerCase().trim();
  const source = String(touch.utm_source || '').toLowerCase().trim();

  if (medium && PAID_MEDIUMS.has(medium)) {
    if (source) return `${source}_cpc`;
    return 'paid';
  }

  if (source) {
    if (source.includes('google') && (medium === 'organic' || !medium)) return 'google_organic';
    if (source.includes('youtube')) return 'youtube';
    if (source.includes('reddit')) return 'reddit';
    if (source.includes('bing')) return 'bing_organic';
    return source;
  }

  const fromReferrer = classifyFromReferrer(normalizeHost(touch.referrer));
  if (fromReferrer) return fromReferrer;

  if (touch.referrer) return 'referral';

  return 'direct';
}

export function isMeaningfulTouch(touch) {
  if (!touch || typeof touch !== 'object') return false;
  return Boolean(
    touch.gclid ||
      touch.gbraid ||
      touch.wbraid ||
      touch.utm_source ||
      touch.utm_medium ||
      touch.utm_campaign ||
      (touch.referrer && !isSameOriginReferrer(touch.referrer))
  );
}

function isSameOriginReferrer(referrer) {
  if (!referrer || typeof window === 'undefined') return false;
  try {
    const refOrigin = new URL(referrer).origin;
    return refOrigin === window.location.origin;
  } catch {
    return false;
  }
}

export function touchesDifferForLastTouch(prev, next) {
  if (!prev) return isMeaningfulTouch(next);
  if (!next) return false;
  if (next.gclid && next.gclid !== prev.gclid) return true;
  if (next.gbraid && next.gbraid !== prev.gbraid) return true;
  if (next.wbraid && next.wbraid !== prev.wbraid) return true;
  const prevSource = String(prev.utm_source || '');
  const nextSource = String(next.utm_source || '');
  if (nextSource && nextSource !== prevSource) return true;
  const prevMedium = String(prev.utm_medium || '');
  const nextMedium = String(next.utm_medium || '');
  if (nextMedium && nextMedium !== prevMedium) return true;
  if (next.referrer && !isSameOriginReferrer(next.referrer)) {
    const prevHost = normalizeHost(prev.referrer);
    const nextHost = normalizeHost(next.referrer);
    if (nextHost && nextHost !== prevHost) return true;
  }
  return false;
}
